// src/components/IndexHeroBanner.js
//
// Editorial hero banner for INDICES + their top constituents.
// Visual template — later can be adapted for thesis-based components.
//
// Design: CDN theme image (Unsplash) + tilted flag watermark + big
// free-floating SVG logos of the top constituents. No circles, no
// backgrounds behind logos — dual drop-shadow filter handles visibility.

import React, { useCallback, useMemo, useState } from 'react';

const LOGO_HOST = '/api/logos';

const C = {
  gold: '#c9a227', purple: '#6660fe',
  green: '#34d399', red: '#f03e3e',
  amber: '#fbbf24', cyan: '#22d3ee',
  white: '#e8e8f0',
};

const FONT_IMPORT = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:ital,wght@0,400;0,600;0,700;1,400&family=Fraunces:ital,wght@0,600;0,700;1,600&display=swap');
`;

// ─── INDICES CONFIG ─────────────────────────────────────────
// Each index: label, subtitle, editorialTag, heroImage, flag, accent,
// and top constituent tickers.
const INDICES = {
  sp500: {
    label: 'S&P 500',
    subtitle: 'Mag 7 · big tech carrega o benchmark global',
    editorialTag: 'S&P 500 · MAG 7',
    heroImage: 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=1800&h=900&fit=crop&q=80',
    flag: 'us',
    accent: '#6660fe',
    tickers: [
      { ticker: 'NVDA',  name: 'NVIDIA' },
      { ticker: 'MSFT',  name: 'Microsoft' },
      { ticker: 'AAPL',  name: 'Apple' },
      { ticker: 'AMZN',  name: 'Amazon' },
      { ticker: 'META',  name: 'Meta' },
      { ticker: 'GOOGL', name: 'Alphabet' },
      { ticker: 'TSLA',  name: 'Tesla' },
    ],
  },
  nasdaq: {
    label: 'Nasdaq 100',
    subtitle: 'Top 7 · quase metade do índice em big tech',
    editorialTag: 'NDX · TOP 7',
    heroImage: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1800&h=900&fit=crop&q=80',
    flag: 'us',
    accent: '#22d3ee',
    tickers: [
      { ticker: 'NVDA',  name: 'NVIDIA' },
      { ticker: 'MSFT',  name: 'Microsoft' },
      { ticker: 'AAPL',  name: 'Apple' },
      { ticker: 'AMZN',  name: 'Amazon' },
      { ticker: 'META',  name: 'Meta' },
      { ticker: 'GOOGL', name: 'Alphabet' },
      { ticker: 'TSLA',  name: 'Tesla' },
    ],
  },
  ibov: {
    label: 'Ibovespa',
    subtitle: 'Blue chips brasileiros — commodities, bancos, consumo',
    editorialTag: 'IBOV · TOP 7',
    heroImage: 'https://images.unsplash.com/photo-1483729558449-99ef09a8c325?w=1800&h=900&fit=crop&q=80',
    flag: 'br',
    accent: '#00d4a0',
    tickers: [
      { ticker: 'VALE3', name: 'Vale' },
      { ticker: 'PETR4', name: 'Petrobras' },
      { ticker: 'ITUB4', name: 'Itaú' },
      { ticker: 'BBDC4', name: 'Bradesco' },
      { ticker: 'ABEV3', name: 'Ambev' },
      { ticker: 'B3SA3', name: 'B3' },
      { ticker: 'WEGE3', name: 'WEG' },
    ],
  },
  dax: {
    label: 'DAX',
    subtitle: 'Locomotivas alemãs — indústria, farma, seguros',
    editorialTag: 'DAX · TOP 7',
    heroImage: 'https://images.unsplash.com/photo-1554072675-66db59dba46f?w=1800&h=900&fit=crop&q=80',
    flag: 'de',
    accent: '#fbbf24',
    tickers: [
      { ticker: 'SAP',    name: 'SAP' },
      { ticker: 'SIE.DE', name: 'Siemens' },
      { ticker: 'ALV.DE', name: 'Allianz' },
      { ticker: 'DTE.DE', name: 'Deutsche Telekom' },
      { ticker: 'AIR.PA', name: 'Airbus' },
      { ticker: 'MUV2.DE',name: 'Munich Re' },
      { ticker: 'MBG.DE', name: 'Mercedes-Benz' },
    ],
  },
  nikkei: {
    label: 'Nikkei 225',
    subtitle: 'Blue chips japoneses — indústria, tech, financeiro',
    editorialTag: 'NIKKEI · TOP 7',
    heroImage: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1800&h=900&fit=crop&q=80',
    flag: 'jp',
    accent: '#f472b6',
    tickers: [
      { ticker: '7203.T', name: 'Toyota' },
      { ticker: '6758.T', name: 'Sony' },
      { ticker: '9432.T', name: 'NTT' },
      { ticker: '8306.T', name: 'Mitsubishi UFJ' },
      { ticker: '6861.T', name: 'Keyence' },
      { ticker: '6098.T', name: 'Recruit' },
      { ticker: '9984.T', name: 'SoftBank' },
    ],
  },
  ftse: {
    label: 'FTSE 100',
    subtitle: 'Large caps de Londres — energia, farma, mineração',
    editorialTag: 'FTSE · TOP 7',
    heroImage: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1800&h=900&fit=crop&q=80',
    flag: 'gb',
    accent: '#a78bfa',
    tickers: [
      { ticker: 'AZN.L',  name: 'AstraZeneca' },
      { ticker: 'SHEL.L', name: 'Shell' },
      { ticker: 'HSBA.L', name: 'HSBC' },
      { ticker: 'ULVR.L', name: 'Unilever' },
      { ticker: 'BP.L',   name: 'BP' },
      { ticker: 'GSK.L',  name: 'GSK' },
      { ticker: 'RIO.L',  name: 'Rio Tinto' },
    ],
  },
};

// ─── FREE-FLOATING LOGO ─────────────────────────────────────
// SVG rendered raw, no background. Dual drop-shadow filter:
//   1. white halo → dark logos (Apple, Amazon) visible on dark bg
//   2. black shadow → depth for light/colored logos
const FloatingLogo = ({ ticker, name, size = 108 }) => {
  const [failed, setFailed] = useState(false);
  const base = (ticker || '').split('.')[0];
  const monogram = (name || ticker || '')
    .replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase() || 'RR';

  if (failed || !base) {
    return (
      <div style={{
        width: size, height: size,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Fraunces', Georgia, serif",
        fontStyle: 'italic',
        fontSize: size * 0.42, fontWeight: 700,
        color: 'rgba(255,255,255,0.92)',
        letterSpacing: '-0.03em',
        textShadow: `
          0 0 18px rgba(255,255,255,0.4),
          0 4px 12px rgba(0,0,0,0.7)
        `,
        userSelect: 'none',
      }}>{monogram}</div>
    );
  }

  return (
    <img
      src={`${LOGO_HOST}/${base}.svg`}
      alt={name || ticker}
      onError={() => setFailed(true)}
      style={{
        width: size, height: size,
        objectFit: 'contain',
        filter: `
          drop-shadow(0 0 10px rgba(255,255,255,0.45))
          drop-shadow(0 4px 10px rgba(0,0,0,0.65))
        `,
        WebkitFilter: `
          drop-shadow(0 0 10px rgba(255,255,255,0.45))
          drop-shadow(0 4px 10px rgba(0,0,0,0.65))
        `,
      }}
    />
  );
};

// ─── LOGO STRIP ─────────────────────────────────────────────
const LogoStrip = ({ tickers, size = 108 }) => (
  <div style={{
    display: 'flex', flexWrap: 'wrap',
    gap: 28, alignItems: 'flex-start', justifyContent: 'flex-start',
  }}>
    {tickers.map(tk => (
      <div key={tk.ticker} style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
        minWidth: size, flexShrink: 0,
      }}>
        <FloatingLogo ticker={tk.ticker} name={tk.name} size={size} />
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.95)',
          letterSpacing: '0.08em',
          textShadow: '0 1px 6px rgba(0,0,0,0.9)',
        }}>{tk.ticker}</div>
        <div style={{
          fontFamily: "'Fraunces', Georgia, serif", fontStyle: 'italic',
          fontSize: 10.5, color: 'rgba(255,255,255,0.7)',
          letterSpacing: '0.005em',
          textShadow: '0 1px 4px rgba(0,0,0,0.9)',
          maxWidth: size + 24, textAlign: 'center',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{tk.name}</div>
      </div>
    ))}
  </div>
);

// ─── MAIN COMPONENT ─────────────────────────────────────────
// Props:
//   indexId   — key from built-in INDICES config (default 'ibov')
//   index     — full override object (optional; wins over indexId)
//   logoSize  — px size for each logo (default 108)
//   showCycle — show the "next index" button (default true if using built-ins)
export const IndexHeroBanner = ({
  indexId = 'ibov',
  index: indexOverride = null,
  logoSize = 108,
  showCycle = true,
}) => {
  const [activeId, setActiveId] = useState(indexId);

  const active = indexOverride || INDICES[activeId] || INDICES.ibov;
  const canCycle = !indexOverride && showCycle;

  const cycleKeys = useMemo(() => Object.keys(INDICES), []);
  const cycleNext = useCallback(() => {
    const i = cycleKeys.indexOf(activeId);
    setActiveId(cycleKeys[(i + 1) % cycleKeys.length]);
  }, [activeId, cycleKeys]);

  return (
    <div style={{
      position: 'relative',
      minHeight: 420,
      borderRadius: 16,
      overflow: 'hidden',
      background: `url(${active.heroImage}) center/cover`,
      fontFamily: "'IBM Plex Mono', monospace",
      color: C.white,
      isolation: 'isolate',
    }}>
      <style>{FONT_IMPORT}{`
        .rr-idx-cycle:hover {
          background: rgba(255,255,255,0.16) !important;
          transform: translateY(-1px);
        }
        .rr-idx-cycle:active { transform: translateY(0); }
        @keyframes rr-idx-fade {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .rr-idx-fade { animation: rr-idx-fade 0.4s ease-out; }
      `}</style>

      {/* Gradient overlay */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `
          linear-gradient(180deg,
            rgba(7,7,10,0.55) 0%,
            rgba(7,7,10,0.20) 30%,
            rgba(7,7,10,0.50) 65%,
            rgba(7,7,10,0.92) 100%
          )
        `,
      }} />

      {/* Flag watermark */}
      {active.flag && (
        <div style={{
          position: 'absolute',
          right: -70, top: 60,
          pointerEvents: 'none',
          opacity: 0.22,
          transform: 'rotate(-14deg)',
          filter: 'grayscale(0.6) contrast(1.05)',
          zIndex: 1,
        }}>
          <img
            src={`https://flagcdn.com/w1280/${active.flag}.png`}
            alt=""
            style={{ width: 520, height: 'auto', display: 'block' }}
          />
        </div>
      )}

      {/* Top bar */}
      <div style={{
        position: 'absolute', top: 22, left: 24, right: 24,
        zIndex: 4,
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      }}>
        <span style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 11, fontWeight: 800, color: C.purple,
          background: 'rgba(102,96,254,0.20)',
          border: '1px solid rgba(102,96,254,0.5)',
          padding: '4px 10px', borderRadius: 3,
          letterSpacing: '0.18em',
          backdropFilter: 'blur(8px)',
        }}>RR MAGMA</span>
        <span style={{
          fontSize: 9, fontWeight: 700, color: active.accent || C.gold,
          background: `${active.accent || C.gold}20`,
          border: `1px solid ${active.accent || C.gold}55`,
          padding: '3px 8px', borderRadius: 3,
          letterSpacing: '0.14em',
          backdropFilter: 'blur(8px)',
        }}>{active.editorialTag}</span>

        {canCycle && (
          <button
            className="rr-idx-cycle"
            onClick={cycleNext}
            style={{
              marginLeft: 'auto',
              background: 'rgba(255,255,255,0.10)',
              border: '1px solid rgba(255,255,255,0.22)',
              borderRadius: 4, color: C.white,
              fontSize: 9, fontWeight: 700,
              padding: '7px 13px', cursor: 'pointer',
              fontFamily: 'inherit', letterSpacing: '0.14em',
              display: 'flex', alignItems: 'center', gap: 6,
              backdropFilter: 'blur(8px)', transition: 'all 0.15s',
            }}
          ><span>↻</span> PRÓXIMO ÍNDICE</button>
        )}
      </div>

      {/* Title stack */}
      <div
        key={activeId}
        className="rr-idx-fade"
        style={{
          position: 'absolute', top: 90, left: 24, right: 24,
          zIndex: 3,
          maxWidth: 720,
        }}
      >
        <h2 style={{
          fontFamily: "'Fraunces', Georgia, serif",
          fontSize: 'clamp(32px, 5vw, 48px)',
          fontWeight: 700, color: C.white,
          margin: 0, lineHeight: 1.05, letterSpacing: '-0.02em',
          textShadow: '0 3px 20px rgba(0,0,0,0.75)',
        }}>{active.label}</h2>
        <div style={{
          fontFamily: "'Fraunces', Georgia, serif", fontStyle: 'italic',
          fontSize: 'clamp(13px, 1.4vw, 17px)',
          color: 'rgba(255,255,255,0.85)',
          marginTop: 10,
          maxWidth: 560,
          textShadow: '0 2px 12px rgba(0,0,0,0.8)',
          lineHeight: 1.4,
        }}>{active.subtitle}</div>
      </div>

      {/* Logo strip */}
      <div
        key={activeId + '-logos'}
        className="rr-idx-fade"
        style={{
          position: 'absolute',
          bottom: 30, left: 24, right: 24,
          zIndex: 3,
        }}
      >
        <div style={{
          height: 1,
          background: `linear-gradient(90deg, ${active.accent || C.gold}00, ${active.accent || C.gold}55, ${active.accent || C.gold}00)`,
          marginBottom: 20,
        }} />
        <div style={{
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 9, letterSpacing: '0.16em',
          color: active.accent || C.gold,
          fontWeight: 700, marginBottom: 16,
          textShadow: '0 1px 4px rgba(0,0,0,0.8)',
        }}>
          {active.tickers.length} COMPONENTES
        </div>
        <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
          <LogoStrip tickers={active.tickers} size={logoSize} />
        </div>
      </div>
    </div>
  );
};

export default IndexHeroBanner;
